import React from 'react';
import { Helmet } from 'react-helmet';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

import helpStep1 from '../assets/images/help-step1-profile.png';
import helpStep2 from '../assets/images/help-step2-settings.png';
import helpStep3 from '../assets/images/help-step3-warning.png';
import helpStep4 from '../assets/images/help-step4-type-delete.png';
import helpStep5 from '../assets/images/help-step5-confirm.png';
import helpStep6 from '../assets/images/help-step6-complete.png';

type Step = {
  number: number;
  title: string;
  image: string;
  alt: string;
  body: React.ReactNode;
};

const steps: Step[] = [
  {
    number: 1,
    title: 'Open Settings',
    image: helpStep1,
    alt: 'Profile screen with Settings icon highlighted',
    body: (
      <ol className="list-decimal pl-5 space-y-1.5">
        <li>Tap your <strong>Profile</strong> tab at the bottom of the screen</li>
        <li>Tap the <strong>Settings icon</strong> (gear icon) in the top right corner</li>
      </ol>
    ),
  },
  {
    number: 2,
    title: 'Navigate to Delete Account',
    image: helpStep2,
    alt: 'Settings screen with Delete Account button highlighted',
    body: (
      <ol className="list-decimal pl-5 space-y-1.5">
        <li>Scroll down to the bottom of the Settings screen</li>
        <li>Tap <strong>Delete Account</strong> (shown in red)</li>
      </ol>
    ),
  },
  {
    number: 3,
    title: 'Review What Will Be Deleted',
    image: helpStep3,
    alt: 'Delete Account screen showing warning and bullet points',
    body: (
      <p>You'll see a warning screen explaining what will happen. Review the list carefully before proceeding.</p>
    ),
  },
  {
    number: 4,
    title: 'Type "delete" to Confirm',
    image: helpStep4,
    alt: 'Text field with delete typed in',
    body: (
      <ol className="list-decimal pl-5 space-y-1.5">
        <li>Read the warning carefully</li>
        <li>In the text field, type the word <code className="font-mono text-[14px] bg-ink/5 text-primary px-2 py-0.5 rounded">delete</code> (lowercase)</li>
        <li>The red "Permanently Delete Account" button will become active</li>
      </ol>
    ),
  },
  {
    number: 5,
    title: 'Confirm Deletion',
    image: helpStep5,
    alt: 'Final confirmation dialog',
    body: (
      <ol className="list-decimal pl-5 space-y-1.5">
        <li>Tap the <strong>Permanently Delete Account</strong> button</li>
        <li>A final confirmation popup will appear: "Are you absolutely sure?"</li>
        <li>Tap <strong>"Yes, Delete Everything"</strong> to proceed, or <strong>"Cancel"</strong> to go back</li>
      </ol>
    ),
  },
  {
    number: 6,
    title: 'Account Deleted',
    image: helpStep6,
    alt: 'Login screen after deletion',
    body: (
      <p>Your account and all associated data will be permanently deleted from our servers. You'll be automatically logged out and returned to the login screen.</p>
    ),
  },
];

const HelpPage: React.FC = () => {
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Help | DINK Social — Account Support</title>
        <meta name="description" content="Get help with your DINK Social account. Find answers to common questions, learn how to manage your profile, and contact our support team." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Help | DINK Social — Account Support" />
        <meta property="og:description" content="Get help with your DINK Social account. Find answers to common questions and contact support." />
        <meta property="og:url" content="https://dinksocial.com/help" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Help | DINK Social — Account Support" />
        <meta name="twitter:description" content="Get help with your DINK Social account." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/help" />
      </Helmet>

      <Nav />

      {/* ─── HEADING ───────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Help center
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            Account support and how-tos
          </p>
        </div>
      </section>

      {/* ─── ACCOUNT DELETION GUIDE ────────────────────────── */}
      <section className="pb-20 md:pb-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Intro */}
          <div className="mb-12">
            <h2
              className="font-display font-medium text-ink text-[28px] md:text-[36px] mb-4"
              style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
            >
              How to delete your account
            </h2>
            <p
              className="text-[17px] md:text-[18px] text-ink/70"
              style={{ lineHeight: 1.55 }}
            >
              Deleting your account is permanent and cannot be undone. Follow these steps to permanently delete your DINK Social account.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-14 md:space-y-16">
            {steps.map((step) => (
              <div key={step.number}>
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 flex-shrink-0">
                    {`Step ${step.number}`}
                  </span>
                  <h3
                    className="font-playfair font-medium text-ink text-[24px] md:text-[28px]"
                    style={{ lineHeight: 1.2, letterSpacing: '-0.015em' }}
                  >
                    {step.title}
                  </h3>
                </div>
                <div
                  className="text-[16px] md:text-[17px] text-ink/70 mb-6"
                  style={{ lineHeight: 1.6 }}
                >
                  {step.body}
                </div>
                <div className="flex justify-center">
                  <img
                    src={step.image}
                    alt={step.alt}
                    className="rounded-3xl border border-ink/10 max-w-sm w-full"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Important Information */}
          <div className="mt-20 md:mt-24">
            <h2
              className="font-display font-medium text-ink text-[28px] md:text-[36px] mb-6"
              style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
            >
              Important information
            </h2>
            <div className="space-y-4">
              <div className="border-l-2 border-primary pl-5 py-1">
                <p className="font-display font-medium text-ink text-[16px] md:text-[17px] mb-1">
                  This action cannot be reversed
                </p>
                <p className="text-[15px] md:text-[16px] text-ink/70" style={{ lineHeight: 1.55 }}>
                  Once deleted, your account and all associated data are permanently removed. There is no way to recover your account, photos, posts, or any other data after deletion.
                </p>
              </div>
              <div className="border-l-2 border-primary pl-5 py-1">
                <p className="font-display font-medium text-ink text-[16px] md:text-[17px] mb-1">
                  Effect on your partner
                </p>
                <p className="text-[15px] md:text-[16px] text-ink/70" style={{ lineHeight: 1.55 }}>
                  If you're in a couple profile, deleting your account will also delete the entire couple profile, affecting your partner's account as well.
                </p>
              </div>
            </div>
          </div>

          {/* Need help */}
          <div className="mt-20 md:mt-24">
            <h2
              className="font-display font-medium text-ink text-[28px] md:text-[36px] mb-4"
              style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
            >
              Still need help
            </h2>
            <p
              className="text-[17px] md:text-[18px] text-ink/70"
              style={{ lineHeight: 1.55 }}
            >
              {"If you're experiencing issues, can't access the app, or have questions, contact our support team at "}
              <a
                href="mailto:support@dinksocial.com"
                className="text-primary hover:text-primary-dark hover:underline font-medium transition-colors"
              >
                support@dinksocial.com
              </a>
              {' before deleting your account, or for help with deleting your account.'}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpPage;
